/**
 * Whether an action button has already been completed by this visitor, and
 * what stands in its place when it has.
 *
 * Lifted out of PublicCustomLayout so the React event detail page answers these
 * questions the same way the Page Builder renderer does. Two copies of "has
 * this visitor registered?" is exactly the kind of divergence that ends with
 * one surface withdrawing a button the other still offers.
 */

/**
 * OFF unless a builder turns it on, per button.
 *
 * Per BUTTON rather than per page because one page can carry both: an event's
 * Register should disappear once you are in, while its "Ask the organiser"
 * button should stay for as long as you want to ask again.
 */
export function hidesWhenCompleted(button) {
  const action = (button && button.action) || {};
  return ((action.on_completed || {}).hide === true);
}

// A success message may carry `{{ table.column }}` tokens that the SERVER fills
// from the rows the write just created — a pass code, a reference number. Those
// values existed only in that response; on a later visit there is nothing to
// substitute, so reusing such a message would show the visitor the raw
// `{{ event_pass.pass_code }}` text. A message with a placeholder is therefore
// not eligible as the fallback.
const HAS_TOKEN_RE = /\{\{[^}]*\}\}/;

/**
 * What stands in place of a button the visitor has already completed.
 *
 * The wording is the button's own configuration, never a string written here:
 * this platform serves any application, and only the application knows whether
 * its rows mean "registered", "booked" or "submitted".
 */
export function completedMessage(button) {
  const action = (button && button.action) || {};
  const configured = (action.on_completed || {}).message;
  if (configured) return configured;

  const success = (action.on_success || {}).message;
  if (success && !HAS_TOKEN_RE.test(success)) return success;

  return 'You have already completed this.';
}

/**
 * Has this visitor already completed this button on this record?
 *
 * EVERY branch fails to FALSE, which draws the button. That is the safe
 * direction: the worst outcome is one wasted click, which the server's own
 * dedupe absorbs into an update of the row that already exists. Failing the
 * other way would hide a button from someone who has NOT registered, and they
 * would have no way to register at all — a far worse failure, and a silent one.
 */
export function decideCompleted(button, doneKeys, recordId) {
  try {
    if (!doneKeys || recordId === null || recordId === undefined) return false;
    if (!button || button.id === undefined || button.id === null) return false;
    // The builder has to have asked for this. Checked here as well as on the
    // server, so turning the setting off puts the button back at once rather
    // than waiting for notes already in a session to expire.
    if (!hidesWhenCompleted(button)) return false;
    // Only a write can be "already done" — a redirect navigates, and is always
    // offered however many times it has been followed.
    const type = button.type || (button.action || {}).type;
    if (type !== 'submit') return false;
    return doneKeys.has(`${recordId}/${button.id}`);
  } catch (e) {
    return false;
  }
}
